import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Avatar } from '@/design-system'
import { avatarGradient } from '@/features/connections/utils'
import { TopicOwner } from '../../types'

export interface CardOwnerBadgeProps {
  owner: TopicOwner
  likes?: number
}

const Bar = styled(Box)({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '22px 12px 10px',
  background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.55))',
  pointerEvents: 'none',
})

const OwnerName = styled(Typography)({
  fontSize: 12,
  fontWeight: 650,
  color: '#fff',
  letterSpacing: '-0.01em',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const Likes = styled(Box)({
  marginLeft: 'auto',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 10.5,
  color: 'rgba(255,255,255,0.85)',
  flexShrink: 0,
})

/**
 * Bottom-of-cover owner attribution — avatar + name + like count. Only ever
 * rendered when `AlbumSummary`/`GallerySummary.owner` is set, i.e. only on
 * public/other-user content (Discover today). `/albums`/`/galleries`/`/search`
 * never populate `owner`, so this stays invisible there — zero behavior
 * change for the "my stuff" grids.
 *
 * Deliberately always the initials-on-gradient fallback (`avatarGradient`,
 * same deterministic six-tone cycle `PersonRow` uses), never the owner's
 * real profile photo — a small photo thumbnail rendered small and dark-veil
 * overlaid on someone else's cover photo reads as noisy/unclear at this
 * size, and the gradient keeps a feed of strangers reading as a legible,
 * consistent crowd instead of a wall of tiny inconsistent headshots.
 */
export function CardOwnerBadge({ owner, likes }: CardOwnerBadgeProps) {
  return (
    <Bar>
      <Avatar
        sx={{
          width: 22,
          height: 22,
          fontSize: 10,
          flexShrink: 0,
          background: avatarGradient(owner.name),
          color: '#fff',
        }}
      >
        {owner.name?.[0]?.toUpperCase()}
      </Avatar>
      <OwnerName>{owner.name}</OwnerName>
      {likes != null && (
        <Likes>
          <span style={{ color: '#fea67d' }}>&#9829;</span> {likes}
        </Likes>
      )}
    </Bar>
  )
}
